import React, { useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Unlink, Heading2, Heading3, Pilcrow, Undo2, Redo2, Palette,
  Braces, Maximize2, Minimize2, AlertTriangle, AlignLeft, AlignCenter, MousePointerClick, Minus, ChevronDown, ClipboardType, FolderOpen } from "lucide-react";
import { AssetPicker } from "./AssetPicker";
import { MERGE_GROUPS } from "./mergeFields";
import { toVisual, fromVisual, chipHtml, unsupportedTags, stripUnsupported, cleanPastedHtml, textToHtml } from "./htmlCodec";

const COLORS = [["Default", "#1e293b"], ["Navy", "#0F2B5B"], ["Blue", "#006090"], ["Green", "#047857"], ["Red", "#b91c1c"], ["Orange", "#c2410c"], ["Gray", "#64748b"]];

const TB = ({ onClick, title, children, testId, active }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} aria-label={title} data-testid={testId}
    className={`h-8 min-w-8 px-1.5 flex items-center justify-center rounded text-slate-600 transition-colors hover:bg-slate-200 ${active ? "bg-slate-200" : ""}`}>{children}</button>
);
const Sep = () => <span className="w-px h-5 bg-slate-200 mx-0.5" />;

export function InsertFieldMenu({ onPick, testId, exclude = [], label = "Insert merge field" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" onMouseDown={(e) => e.preventDefault()} data-testid={testId}
          className="h-8 px-2.5 flex items-center gap-1 rounded text-xs font-semibold text-sky-800 bg-sky-50 border border-sky-200 hover:bg-sky-100">
          <Braces className="w-3.5 h-3.5" />{label}<ChevronDown className="w-3 h-3" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white w-60 max-h-80 overflow-auto">
        {MERGE_GROUPS.map(([g, fields], i) => (
          <React.Fragment key={g}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">{g}</DropdownMenuLabel>
            {fields.filter(([k]) => !exclude.includes(k)).map(([k, l]) => (
              <DropdownMenuItem key={k} onSelect={() => onPick(k)} data-testid={`${testId}-${k}`} className="flex flex-col items-start gap-0">
                <span>{l}</span><span className="font-mono text-[10px] text-slate-400">{`{{${k}}}`}</span>
              </DropdownMenuItem>))}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ColorMenu({ onPick, testId }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" onMouseDown={(e) => e.preventDefault()} title="Text color" aria-label="Text color" data-testid={testId}
          className="h-8 w-8 flex items-center justify-center rounded text-slate-600 hover:bg-slate-200"><Palette className="w-4 h-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-white w-40">
        {COLORS.map(([n, c]) => <DropdownMenuItem key={c} onSelect={() => onPick(c)} data-testid={`${testId}-${n.toLowerCase()}`}>
          <span className="w-3.5 h-3.5 rounded-full mr-2 border" style={{ background: c }} />{n}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LinkBar({ init, onApply, onCancel, testId }) {
  const [url, setUrl] = useState(init.url || "https://");
  const [text, setText] = useState(init.text || "");
  return (
    <div className="flex flex-wrap items-center gap-2 px-2 py-2 bg-sky-50 border-b border-sky-100" data-testid={`${testId}-bar`}>
      <Input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="h-8 flex-1 min-w-[180px] bg-white" data-testid={`${testId}-url`}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onApply(url, text); } }} />
      {init.collapsed && <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Link text" className="h-8 w-44 bg-white" data-testid={`${testId}-text`} />}
      <Button size="sm" className="h-8 bg-brand hover:bg-brand-strong" onClick={() => onApply(url, text)} data-testid={`${testId}-apply`}>Apply</Button>
      <Button size="sm" variant="ghost" className="h-8" onClick={onCancel}>Cancel</Button>
    </div>
  );
}

export function EmailBodyEditor({ value, onChange, renderPreview, expanded, onExpandChange, testId = "email",
  showFields = true, cleanPaste = false, modes = [["design", "Visual"], ["html", "HTML"], ["preview", "Preview"]], compact = false, onAttachAsset }) {
  const [mode, setMode] = useState("design");
  const [picker, setPicker] = useState(false);
  const [warn, setWarn] = useState(null);
  const [link, setLink] = useState(null);
  const [plainPaste, setPlainPaste] = useState(false);
  const el = useRef(null);
  const ta = useRef(null);
  const range = useRef(null);
  const emitted = useRef(null);

  useEffect(() => {
    if (mode === "design" && el.current && value !== emitted.current) {
      el.current.innerHTML = toVisual(value || "");
      emitted.current = value;
    }
  }, [value, mode]);

  const sync = () => { if (!el.current) return; const h = fromVisual(el.current); emitted.current = h; onChange(h); };
  const save = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && el.current?.contains(s.anchorNode)) range.current = s.getRangeAt(0).cloneRange();
  };
  const restore = () => {
    el.current?.focus();
    if (range.current) { const s = window.getSelection(); s.removeAllRanges(); s.addRange(range.current); }
  };
  const exec = (cmd, arg) => {
    restore();
    try { document.execCommand("styleWithCSS", false, cmd === "foreColor"); } catch { /* unsupported */ }
    document.execCommand(cmd, false, arg); save(); sync();
  };

  const insertField = (k) => {
    if (mode === "html") {
      const t = ta.current; const v = value || ""; const pos = t ? t.selectionStart : v.length;
      onChange(v.slice(0, pos) + `{{${k}}}` + v.slice(pos)); return;
    }
    if (mode === "preview") setMode("design");
    setTimeout(() => exec("insertHTML", chipHtml(k) + "&#8203;"), 0);
  };

  const openLink = () => {
    save();
    const s = window.getSelection();
    const a = s?.anchorNode?.parentElement?.closest?.("a");
    setLink({ url: a?.getAttribute("href") || "", text: "", collapsed: !s || s.isCollapsed });
  };
  const applyLink = (url, text) => {
    const u = url.trim(); setLink(null);
    if (!u || u === "https://") return;
    const safe = u.replace(/"/g, "%22");
    if (range.current && !range.current.collapsed) exec("createLink", safe);
    else exec("insertHTML", `<a href="${safe}">${(text || u).replace(/</g, "&lt;")}</a>&nbsp;`);
  };
  const addButton = () => {
    save();
    const url = window.prompt("Button link (https://…)"); if (!url) return;
    const label = (window.prompt("Button text", "Schedule your interview") || "Click here").replace(/</g, "&lt;");
    exec("insertHTML", `<a href="${url.replace(/"/g, "%22")}" style="display:inline-block;background:#0F2B5B;color:#ffffff;padding:10px 18px;border-radius:6px;text-decoration:none;font-weight:600;margin:6px 0">${label}</a>&nbsp;`);
  };

  const insertAssetHtml = (html) => {
    if (mode === "html") {
      const t = ta.current; const v = value || ""; const pos = t ? t.selectionStart : v.length;
      onChange(v.slice(0, pos) + html + v.slice(pos)); return;
    }
    exec("insertHTML", html);
  };

  const switchTo = (m) => {
    if (m === mode) return;
    if (mode === "design") sync();
    if (mode === "html" && m === "design") {
      const bad = unsupportedTags(value || "");
      if (bad.length) { setWarn({ tags: bad, to: m }); return; }
    }
    setWarn(null); setLink(null); emitted.current = null; setMode(m);
  };
  const confirmStrip = () => { const to = warn.to; onChange(stripUnsupported(value || "")); setWarn(null); emitted.current = null; setMode(to); };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); document.execCommand("insertLineBreak"); sync(); }
    else if (e.key === "Enter" && !e.shiftKey) {
      const node = window.getSelection()?.anchorNode;
      const li = (node?.nodeType === 1 ? node : node?.parentElement)?.closest?.("li");
      if (!li) { e.preventDefault(); document.execCommand("insertParagraph"); sync(); }
      else if (!li.textContent.replace(/\u200B/g, "").trim()) {
        e.preventDefault();
        document.execCommand(li.parentElement?.tagName === "OL" ? "insertOrderedList" : "insertUnorderedList");
        document.execCommand("formatBlock", false, "<p>"); sync();
      }
    }
  };
  const onFocus = () => { try { document.execCommand("defaultParagraphSeparator", false, "p"); } catch { /* unsupported */ } };
  const onPaste = (e) => {
    if (!cleanPaste && !plainPaste) return;
    const cd = e.clipboardData; if (!cd) return;
    const html = cd.getData("text/html");
    const text = cd.getData("text/plain");
    e.preventDefault();
    document.execCommand("insertHTML", false, (!plainPaste && html) ? cleanPastedHtml(html) : textToHtml(text));
    save(); sync();
  };

  const height = expanded ? "h-[58vh]" : compact ? "min-h-[160px] max-h-[40vh]" : "min-h-[260px] max-h-[44vh]";

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={`${testId}-editor-wrap`}>
      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-100 px-1.5 py-1 border-b">
        <div className="flex gap-0.5 bg-slate-200/60 rounded-md p-0.5">
          {modes.map(([m, label]) => (
            <button key={m} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => switchTo(m)} data-testid={`${testId}-mode-${m}`}
              className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {cleanPaste && mode === "design" && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setPlainPaste((v) => !v)}
            data-testid={`${testId}-plain-paste`} aria-pressed={plainPaste} title="When on, pasted content keeps only its text and line breaks"
            className={`h-8 px-2.5 flex items-center gap-1 rounded text-xs font-semibold border transition-colors ${plainPaste ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <ClipboardType className="w-3.5 h-3.5" />Paste as plain text{plainPaste ? ": On" : ""}</button>}
          {onAttachAsset && mode !== "preview" && <button type="button" onMouseDown={(e) => { e.preventDefault(); save(); }} onClick={() => setPicker(true)} data-testid={`${testId}-insert-asset`}
            className="h-8 px-2.5 flex items-center gap-1 rounded text-xs font-semibold border bg-white border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"><FolderOpen className="w-3.5 h-3.5" /><span className="whitespace-nowrap">Insert from Files & Assets</span></button>}
          {showFields && mode !== "preview" && <InsertFieldMenu onPick={insertField} testId={`${testId}-insert-field`} />}
          {onExpandChange && <TB testId={`${testId}-expand`} title={expanded ? "Smaller editor" : "Larger editor"} onClick={() => onExpandChange(!expanded)}>
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</TB>}
        </div>
      </div>

      {warn && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900" data-testid={`${testId}-convert-warning`}>
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-[200px]">The visual view can't keep these HTML elements: <b>{warn.tags.map((t) => `<${t}>`).join(", ")}</b>. Switching removes them (their text stays). Email clients also drop them when sent.</span>
          <Button size="sm" variant="outline" className="h-7" onClick={() => setWarn(null)} data-testid={`${testId}-convert-cancel`}>Stay in HTML</Button>
          <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700" onClick={confirmStrip} data-testid={`${testId}-convert-confirm`}>Remove & switch</Button>
        </div>
      )}

      {mode === "design" && <>
        <div className="flex flex-wrap items-center gap-0.5 bg-slate-50 border-b px-1.5 py-1" data-testid={`${testId}-toolbar`}>
          <TB testId={`${testId}-undo`} title="Undo" onClick={() => exec("undo")}><Undo2 className="w-4 h-4" /></TB>
          <TB testId={`${testId}-redo`} title="Redo" onClick={() => exec("redo")}><Redo2 className="w-4 h-4" /></TB>
          <Sep />
          <TB testId={`${testId}-h2`} title="Heading" onClick={() => exec("formatBlock", "<h2>")}><Heading2 className="w-4 h-4" /></TB>
          <TB testId={`${testId}-h3`} title="Subheading" onClick={() => exec("formatBlock", "<h3>")}><Heading3 className="w-4 h-4" /></TB>
          <TB testId={`${testId}-p`} title="Normal text" onClick={() => exec("formatBlock", "<p>")}><Pilcrow className="w-4 h-4" /></TB>
          <Sep />
          <TB testId={`${testId}-bold`} title="Bold (Ctrl+B)" onClick={() => exec("bold")}><Bold className="w-4 h-4" /></TB>
          <TB testId={`${testId}-italic`} title="Italic (Ctrl+I)" onClick={() => exec("italic")}><Italic className="w-4 h-4" /></TB>
          <TB testId={`${testId}-underline`} title="Underline (Ctrl+U)" onClick={() => exec("underline")}><Underline className="w-4 h-4" /></TB>
          <ColorMenu testId={`${testId}-color`} onPick={(c) => exec("foreColor", c)} />
          <Sep />
          <TB testId={`${testId}-ul`} title="Bulleted list" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" /></TB>
          <TB testId={`${testId}-ol`} title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="w-4 h-4" /></TB>
          <TB testId={`${testId}-left`} title="Align left" onClick={() => exec("justifyLeft")}><AlignLeft className="w-4 h-4" /></TB>
          <TB testId={`${testId}-center`} title="Align center" onClick={() => exec("justifyCenter")}><AlignCenter className="w-4 h-4" /></TB>
          <Sep />
          <TB testId={`${testId}-link`} title="Insert link" onClick={openLink}><Link2 className="w-4 h-4" /></TB>
          <TB testId={`${testId}-unlink`} title="Remove link" onClick={() => exec("unlink")}><Unlink className="w-4 h-4" /></TB>
          <TB testId={`${testId}-button`} title="Insert button" onClick={addButton}><MousePointerClick className="w-4 h-4" /></TB>
          <TB testId={`${testId}-hr`} title="Divider" onClick={() => exec("insertHorizontalRule")}><Minus className="w-4 h-4" /></TB>
        </div>
        {link && <LinkBar init={link} testId={`${testId}-link`} onApply={applyLink} onCancel={() => setLink(null)} />}
        <div ref={el} contentEditable suppressContentEditableWarning data-testid={`${testId}-design-editor`} role="textbox" aria-multiline="true" aria-label="Email body"
          onInput={() => { save(); sync(); }} onBlur={sync} onKeyUp={save} onMouseUp={save} onKeyDown={onKeyDown} onFocus={onFocus} onPaste={onPaste}
          className={`${height} overflow-auto px-4 py-3 text-sm leading-relaxed text-slate-800 focus:outline-none [&_table]:border-collapse [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-slate-200 [&_th]:px-2 [&_th]:py-1 [&_pre]:bg-slate-50 [&_pre]:p-2 [&_pre]:rounded [&_pre]:whitespace-pre-wrap [&_h1]:text-xl [&_h1]:font-bold [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:my-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:my-2 [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_a]:text-brand [&_a]:underline [&_hr]:my-3 [&_img]:inline-block [&_img]:max-w-full`} />
        <p className="px-3 py-1.5 text-[11px] text-slate-400 border-t bg-slate-50">Enter starts a new paragraph · Shift+Enter adds a line break</p>
      </>}

      {mode === "html" && <Textarea ref={ta} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-body`}
        className={`${expanded ? "h-[62vh]" : "min-h-[300px]"} rounded-none border-0 font-mono text-xs focus-visible:ring-0`} />}

      {mode === "preview" && <div className={`${expanded ? "h-[62vh]" : "max-h-[50vh] min-h-[260px]"} overflow-auto`} data-testid={`${testId}-preview-pane`}>{renderPreview()}</div>}
      {picker && <AssetPicker onClose={() => setPicker(false)} onInsertHtml={insertAssetHtml} onAttach={onAttachAsset} />}
    </div>
  );
}
